/**
 * Currency Format Configuration
 *
 * Stores the vendor's currency preference from business details so dashboard
 * surfaces render prices in the vendor's currency instead of hardcoded USD.
 * Mirrors the pattern in config/dateFormat.js (sessionStorage cache + fetch).
 */

export const DEFAULT_CURRENCY_CODE = "USD";
export const DEFAULT_CURRENCY_SYMBOL = "$";

// Fallback symbols when the business profile has a code but no symbol.
// Keep in sync with backend features/agent/core/currency.py.
const SYMBOL_BY_CODE = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  INR: "₹",
  PKR: "Rs.",
  AED: "AED",
  JPY: "¥",
  CNY: "¥",
  BRL: "R$",
  CAD: "C$",
  AUD: "A$",
};

/**
 * Get the current currency code and symbol.
 * @returns {{code: string, symbol: string}}
 */
export const getCurrency = () => {
  const code = sessionStorage.getItem("business_currency_code");
  if (code) {
    const symbol =
      sessionStorage.getItem("business_currency_symbol") ||
      SYMBOL_BY_CODE[code] ||
      code;
    return { code, symbol };
  }
  return { code: DEFAULT_CURRENCY_CODE, symbol: DEFAULT_CURRENCY_SYMBOL };
};

/**
 * Set the currency and notify listeners (mounted pages re-render on the event).
 * @param {string} code - ISO 4217 code, e.g. "PKR"
 * @param {string} [symbol] - Display symbol, e.g. "Rs."; derived from code if omitted
 */
export const setCurrency = (code, symbol) => {
  if (!code) return;
  const resolvedSymbol = symbol || SYMBOL_BY_CODE[code] || code;
  sessionStorage.setItem("business_currency_code", code);
  sessionStorage.setItem("business_currency_symbol", resolvedSymbol);
  window.dispatchEvent(new CustomEvent("currencyUpdated"));
};

/**
 * Fetch the vendor's currency from business details and cache it.
 * @returns {Promise<{code: string, symbol: string}>}
 */
export const fetchCurrencyFromBusiness = async () => {
  try {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const headers = { "Content-Type": "application/json" };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${baseUrl}/business_info/vendor/`, {
      method: "GET",
      headers,
    });

    if (response.ok) {
      const result = await response.json();
      const data = result?.data || result;
      if (data?.currency_code) {
        setCurrency(data.currency_code, data.currency_symbol);
      }
      // Cache the business timezone (IANA name, e.g. "Asia/Karachi") so chat
      // timestamps can render in the store's local time. Chat parsing defaults
      // to UTC when this is absent.
      if (data?.timezone) {
        try {
          sessionStorage.setItem("business_timezone", data.timezone);
        } catch (_) {}
      }
    }
  } catch (error) {
    console.error("Error fetching currency from business details:", error);
  }
  return getCurrency();
};

/**
 * Format an amount in the vendor's currency, e.g. "Rs.13,750.00" / "$589.00".
 * Matches the backend bot's CurrencyFormatter output so chat and dashboard agree.
 * @param {number|string} amount
 * @returns {string}
 */
export const formatCurrency = (amount) => {
  const numeric = typeof amount === "string" ? parseFloat(amount) : Number(amount);
  const safe = Number.isFinite(numeric) ? numeric : 0;
  const { symbol } = getCurrency();
  const formatted = safe.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol}${formatted}`;
};

export default {
  getCurrency,
  setCurrency,
  fetchCurrencyFromBusiness,
  formatCurrency,
  DEFAULT_CURRENCY_CODE,
  DEFAULT_CURRENCY_SYMBOL,
};
